import { Add, Edit, Key as KeyIcon, Refresh } from '@mui/icons-material'
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  ApiKeyListItemDto,
  invalidateApiKeysCache,
  useGetApiKeys,
  useRevokeApiKey,
  useRotateApiKey,
} from 'src/api/api-keys'
import { useGetLocalitiesMinimalData } from 'src/api/localities'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import GenericTable from 'src/components/table/GenericTable'
import { useCustomerCtx, useDialogCtx } from 'src/hooks/context-hooks'
import { describeAccess } from './ApiAccessPage.action'
import CreateApiKeyDialog from './CreateApiKeyDialog'
import EditApiKeyLocalitiesDialog from './EditApiKeyLocalitiesDialog'
import { IssuedSecretBanner } from './IssuedSecretBanner'

type Secret = { key: string; secret: string }

const ApiAccessPage = () => {
  const { t } = useTranslation(['api-access', 'common', 'common-api-message'])
  const { customer } = useCustomerCtx()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const customerId = customer.id as EntityId
  const [issuedSecret, setIssuedSecret] = useState<Secret | null>(null)

  const { data, isLoading } = useGetApiKeys(customerId)
  const { data: localities } = useGetLocalitiesMinimalData(customerId)
  const rotateMut = useRotateApiKey(customerId)
  const revokeMut = useRevokeApiKey(customerId)

  const showApiError = useCallback(
    (error: any) => toast.error(t(error?.data || 'unExpectedError', { ns: 'common-api-message' })),
    [t],
  )

  const openCreateDialog = () => {
    showDialog(
      CreateApiKeyDialog,
      { componentProps: { customerId, onCreated: setIssuedSecret }, opts: { maxWidth: 'sm', fullWidth: true } },
      undefined,
      true,
    )
  }

  const openEditLocalitiesDialog = useCallback(
    (apiKey: ApiKeyListItemDto) => {
      showDialog(
        EditApiKeyLocalitiesDialog,
        { componentProps: { customerId, apiKey }, opts: { maxWidth: 'sm', fullWidth: true } },
        undefined,
        true,
      )
    },
    [customerId, showDialog],
  )

  const onRotate = useCallback(
    (id: number) =>
      showConfirmDialog(
        t('rotate-confirm-title', { ns: 'api-access' }),
        t('rotate-confirm-text', { ns: 'api-access' }),
        { acceptText: t('rotate', { ns: 'api-access' }), cancelText: t('cancel', { ns: 'common' }) },
        pop =>
          rotateMut.mutate(id, {
            onSuccess: res => {
              setIssuedSecret({ key: res.key, secret: res.secret })
              toast.success(t('rotate-success', { ns: 'api-access' }))
              invalidateApiKeysCache.list(customerId)
              pop()
            },
            onError: error => {
              showApiError(error)
              pop()
            },
          }),
      ),
    [t, showConfirmDialog, rotateMut, customerId, showApiError],
  )

  const onRevoke = useCallback(
    (id: number) =>
      showConfirmDialog(
        t('revoke-confirm-title', { ns: 'api-access' }),
        t('revoke-confirm-text', { ns: 'api-access' }),
        { acceptText: t('revoke', { ns: 'api-access' }), cancelText: t('cancel', { ns: 'common' }) },
        pop =>
          revokeMut.mutate(id, {
            onSuccess: () => {
              toast.success(t('revoke-success', { ns: 'api-access' }))
              invalidateApiKeysCache.list(customerId)
              pop()
            },
            onError: error => {
              showApiError(error)
              pop()
            },
          }),
      ),
    [t, showConfirmDialog, revokeMut, customerId, showApiError],
  )

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('name', { ns: 'api-access' }) as string,
        flex: 1,
        valueGetter: p => p.row.name || '-',
      },
      {
        field: 'key',
        headerName: t('key-header', { ns: 'api-access' }) as string,
        flex: 1,
        valueGetter: p => p.row.key,
      },
      {
        field: 'grants',
        headerName: t('grants-header', { ns: 'api-access' }) as string,
        flex: 1.5,
        sortable: false,
        filterable: false,
        valueGetter: p =>
          describeAccess(p.row, localities ?? [], { allLocalities: t('all-localities', { ns: 'api-access' }) }) ||
          t('no-localities', { ns: 'api-access' }),
      },
      {
        field: 'createdAt',
        headerName: t('created', { ns: 'api-access' }) as string,
        flex: 1,
        valueGetter: p => new Date(p.row.createdAt).toLocaleString(),
      },
      {
        field: 'lastUsedAt',
        headerName: t('last-used', { ns: 'api-access' }) as string,
        flex: 1,
        valueGetter: p => (p.row.lastUsedAt ? new Date(p.row.lastUsedAt).toLocaleString() : '-'),
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 220,
        renderCell: params => (
          <Stack direction="row" spacing={1} justifyContent="flex-end" width="100%">
            <Tooltip title={String(t('edit-localities', { ns: 'api-access' }))}>
              <IconButton size="small" onClick={() => openEditLocalitiesDialog(params.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={String(t('rotate', { ns: 'api-access' }))}>
              <span>
                <IconButton size="small" onClick={() => onRotate(params.row.id)} disabled={rotateMut.isLoading}>
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={String(t('revoke', { ns: 'api-access' }))}>
              <span>
                <Button
                  color="error"
                  size="small"
                  onClick={() => onRevoke(params.row.id)}
                  disabled={revokeMut.isLoading}
                >
                  {t('revoke', { ns: 'api-access' })}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [t, localities, rotateMut.isLoading, revokeMut.isLoading, onRotate, onRevoke, openEditLocalitiesDialog],
  )

  return (
    <CommonPageLayout titleSection={{ title: t('title', { ns: 'api-access' }), icon: <KeyIcon /> }}>
      <Paper sx={{ p: 3, height: '100%' }}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('existing', { ns: 'api-access' })}</Typography>
            <GridActionButton
              buttonIcon={<Add color="primary" />}
              buttonText={t('create', { ns: 'api-access' })}
              toolTipTitle={t('create-new', { ns: 'api-access' })}
              onClick={openCreateDialog}
              variant="contained"
            />
          </Stack>

          {issuedSecret && <IssuedSecretBanner issuedSecret={issuedSecret} onClose={() => setIssuedSecret(null)} />}

          <Box>
            <GenericTable
              id="api-keys"
              data={!isLoading && data ? data : []}
              columns={columns}
              enablePagination={false}
              hideDefaultFooterPagination
              minRowHeight="40px"
              getRowId={row => row.id}
            />
          </Box>
        </Stack>
      </Paper>
    </CommonPageLayout>
  )
}

export default ApiAccessPage
